// Chart.js option factories for the analytics module.
// Pure functions: given resolved theme primitives (from useChartTheme) plus a little
// per-chart config, return a chart.js options object. Build them inside a `computed`
// in the view so they rebuild when dark mode toggles.

import { formatCompact } from '@/utils/format'

function baseTooltip(t, valueSuffix = '') {
  return {
    backgroundColor: t.tooltipBg,
    titleColor: t.tooltipColor,
    bodyColor: t.tooltipColor,
    borderColor: t.tooltipBorder,
    borderWidth: 1,
    padding: 12,
    cornerRadius: 8,
    titleFont: { family: t.fontFamily, size: 12, weight: '600' },
    bodyFont: { family: t.fontFamily, size: 12 },
    displayColors: false,
    callbacks: {
      label: (ctx) => {
        const v = ctx.parsed.y ?? ctx.parsed.x ?? ctx.parsed ?? 0
        return ` ${Number(v).toLocaleString()}${valueSuffix}`
      },
    },
  }
}

/** Line / area trend chart (single series). */
export function lineChartOptions(t, { valueSuffix = '' } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: { display: false },
      tooltip: baseTooltip(t, valueSuffix),
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { family: t.fontFamily, size: 11 }, color: t.axisColor },
      },
      y: {
        beginAtZero: true,
        grid: { color: t.gridColor },
        border: { display: false },
        ticks: {
          font: { family: t.fontFamily, size: 11 },
          color: t.axisColor,
          maxTicksLimit: 5,
          callback: (v) => formatCompact(v),
        },
      },
    },
  }
}

/** Vertical bar chart (single series). */
export function barChartOptions(t, { valueSuffix = '' } = {}) {
  const o = lineChartOptions(t, { valueSuffix })
  return o
}

/** Horizontal bar chart — for ranked comparisons (group members, top locations). */
export function horizontalBarChartOptions(t, { valueSuffix = '' } = {}) {
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: baseTooltip(t, valueSuffix),
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: t.gridColor },
        border: { display: false },
        ticks: {
          font: { family: t.fontFamily, size: 11 },
          color: t.axisColor,
          maxTicksLimit: 5,
          callback: (v) => formatCompact(v),
        },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { family: t.fontFamily, size: 11 }, color: t.axisColor },
      },
    },
  }
}

/** Doughnut / share chart. */
export function doughnutChartOptions(t, { valueSuffix = '' } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 16,
          color: t.axisColor,
          font: { family: t.fontFamily, size: 12 },
        },
      },
      tooltip: {
        ...baseTooltip(t, valueSuffix),
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${Number(ctx.parsed).toLocaleString()}${valueSuffix}`,
        },
      },
    },
  }
}
